// app/src/lib/scale/members.js — multi-user membership + invitations (US-5.3).
//
// Concepts:
//   - The original Cognito sub for a business account = `ownerId`.
//   - When the owner invites a teammate, an `invitation` row is created with a
//     hash of the invitee's email (we never store raw email at rest in Dexie).
//   - When the invitee signs in for the first time, the Lambda exchange handler
//     resolves the invitation token (a separate code we never write here) and
//     calls `acceptInvitation(invitationId, memberId)`.
//   - Accountant role takes an `expiresAt` (US-5.3): we surface that on every read.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { isValidRole, can, require_ } from './rbac.js';
import { sha256Hex } from '../crypto.js';

/** @typedef {'pending'|'active'|'revoked'|'expired'} MemberStatus */
/** @typedef {{ memberRowId:string, ownerId:string, memberId:string, role:string, status:MemberStatus, expiresAt?:number, addedAt:number }} Member */
/** @typedef {{ invitationId:string, ownerId:string, emailHash:string, role:string, status:'pending'|'accepted'|'revoked'|'expired', expiresAt:number, createdAt:number }} Invitation */

const INVITATION_LIFETIME_MS = 14 * 24 * 60 * 60 * 1000;

async function actor() {
  const claims = await currentClaims();
  if (!claims) throw new Error('member: not signed in');
  return { actorId: claims.sub, role: (claims['custom:role'] || 'owner') };
}

/** Owner-only: create an invitation. Returns the invitation row + the secret token. */
export async function inviteByEmail(email, role, opts = {}) {
  if (!email || typeof email !== 'string') throw new Error('member.invite: email required');
  if (!isValidRole(role)) throw new Error('member.invite: invalid role');
  const { actorId, role: actorRole } = await actor();
  require_(actorRole, 'user.invite');

  if (role === 'accountant' && !opts.expiresAt) throw new Error('member.invite: accountant role requires expiresAt');

  const ownerId = actorId; // Owner invites; one business per owner.
  const emailHash = await sha256Hex(email.trim().toLowerCase());
  const invitationId = crypto.randomUUID();
  const inv = {
    invitationId,
    ownerId,
    emailHash,
    role,
    status: 'pending',
    expiresAt: Date.now() + INVITATION_LIFETIME_MS,
    createdAt: Date.now(),
  };
  await db.invitations.put(inv);
  // The opaque token returned to the caller is the invitationId hashed with the email.
  // The server-side accept Lambda re-derives this and matches; we don't store it.
  const token = await sha256Hex(`${invitationId}|${emailHash}`);
  return { invitation: inv, token };
}

/** Server flow calls this after signing in the invitee, before granting access. */
export async function acceptInvitation(invitationId, memberId, presentedToken, email) {
  const inv = await db.invitations.get(invitationId);
  if (!inv) throw new Error('member.accept: invitation not found');
  if (inv.status !== 'pending') throw new Error('member.accept: invitation not pending');
  if (inv.expiresAt < Date.now()) throw new Error('member.accept: invitation expired');

  // Verify the presented token matches what we'd derive from the stored emailHash.
  const expected = await sha256Hex(`${invitationId}|${inv.emailHash}`);
  if (presentedToken !== expected) throw new Error('member.accept: token mismatch');

  // Defense-in-depth: confirm the email the invitee signed in with hashes to the same value.
  const checkHash = await sha256Hex((email || '').trim().toLowerCase());
  if (checkHash !== inv.emailHash) throw new Error('member.accept: email mismatch');

  const member = {
    memberRowId: crypto.randomUUID(),
    ownerId: inv.ownerId,
    memberId,
    role: inv.role,
    status: 'active',
    expiresAt: inv.role === 'accountant' ? (Date.now() + 30 * 24 * 60 * 60 * 1000) : undefined,
    addedAt: Date.now(),
  };
  await db.transaction('rw', db.members, db.invitations, async () => {
    await db.members.put(member);
    inv.status = 'accepted';
    await db.invitations.put(inv);
  });
  return member;
}

/** Owner-only: revoke a member. */
export async function revoke(memberRowId) {
  const { actorId, role } = await actor();
  require_(role, 'user.manage');
  const m = await db.members.get(memberRowId);
  if (!m || m.ownerId !== actorId) throw new Error('member.revoke: not found');
  m.status = 'revoked';
  await db.members.put(m);
  return m;
}

/** Whether a member is currently active (accountant expiry applied). */
export function isMemberActive(member) {
  if (!member) return false;
  if (member.status !== 'active') return false;
  if (member.expiresAt && member.expiresAt < Date.now()) return false;
  return true;
}

/** Lookup the current actor's role on a given owner's business. */
export async function effectiveRole(ownerId) {
  const claims = await currentClaims();
  if (!claims) return null;
  if (claims.sub === ownerId) return 'owner';
  const m = await db.members.where('[memberId+ownerId]').equals([claims.sub, ownerId]).first();
  if (!m || !isMemberActive(m)) return null;
  return m.role;
}

/** Server- and client-side gate: throws FORBIDDEN if the actor cannot perform `capability` on `ownerId`. */
export async function gate(ownerId, capability) {
  const role = await effectiveRole(ownerId);
  if (!role || !can(role, capability)) {
    const err = new Error(`Forbidden: ${capability}`);
    err.code = 'FORBIDDEN';
    throw err;
  }
  return role;
}

export async function listMembers() {
  const { actorId, role } = await actor();
  require_(role, 'user.manage');
  return db.members.where('[ownerId+status]').anyOf([[actorId, 'active'], [actorId, 'pending']]).toArray();
}
